/**
 * The plans: four levels and the seven ways to be on one.
 *
 * Free, and three subscriptions — Premium, AI Standard and AI Max — each monthly or yearly. A
 * level includes everything below it, so a question about access is always "is this account at
 * least at level X", never a list of plans to check one by one.
 *
 * Shared by the server, which decides what the AI may spend for an account, and by the clients,
 * which say what each plan includes. The prices are not here: a price is what the store charges,
 * in the buyer's currency, and it is read from the store at the moment it is shown.
 */
/** The four levels, lowest first. */
export const PLAN_TIERS = ['free', 'premium', 'ai', 'ai_max'];
/** Whether an account at `tier` has what `needed` includes. */
export function tierIncludes(tier, needed) {
    return PLAN_TIERS.indexOf(tier) >= PLAN_TIERS.indexOf(needed);
}
/** The higher of two levels. */
export function higherTier(a, b) {
    return tierIncludes(a, b) ? a : b;
}
export function isPlanTier(value) {
    return typeof value === 'string' && PLAN_TIERS.includes(value);
}
/**
 * AI credits each level includes a month.
 *
 * A credit is a thousandth of a dollar of model cost (`MICRO_USD_PER_CREDIT`), measured by the
 * tokens each request actually used, so a bigger model uses more of the same allowance rather
 * than needing an allowance of its own. AI Max is five times AI Standard.
 */
export const AI_MONTHLY_CREDITS = {
    free: 50,
    premium: 300,
    ai: 4000,
    ai_max: 20000,
};
/** A credit, in millionths of a dollar: US$ 0.001. */
export const MICRO_USD_PER_CREDIT = 1000;
/**
 * The credits one request used: its cost, rounded up, and never less than one.
 *
 * `microUsdPerCredit` is what a credit is worth to this account (`creditValue`); the nominal
 * thousandth of a dollar when it is not given.
 */
export function creditsForCost(costMicroUsd, microUsdPerCredit = MICRO_USD_PER_CREDIT) {
    if (costMicroUsd === null || costMicroUsd === undefined || !Number.isFinite(costMicroUsd)) {
        return 1;
    }
    const value = Number.isFinite(microUsdPerCredit) && microUsdPerCredit >= 1
        ? Math.floor(microUsdPerCredit)
        : MICRO_USD_PER_CREDIT;
    return Math.max(1, Math.ceil(costMicroUsd / value));
}
/**
 * What one credit is worth to an account, in millionths of a dollar.
 *
 * **The same number of credits everywhere, a different dollar value behind each.** The store sells
 * a plan for less where people earn less, but a model costs the same dollars in every country. So
 * the dollars an account may spend on models in a month (`budgetMicroUsd`) follow what it paid,
 * and the credits a plan shows are that budget cut into the plan's count. A credit is never worth
 * more than the nominal thousandth of a dollar, and never less than a millionth.
 */
export function creditValue(budgetMicroUsd, monthlyCredits) {
    if (!Number.isFinite(budgetMicroUsd) || !Number.isFinite(monthlyCredits) || monthlyCredits <= 0) {
        return MICRO_USD_PER_CREDIT;
    }
    return Math.min(MICRO_USD_PER_CREDIT, Math.max(1, Math.floor(budgetMicroUsd / monthlyCredits)));
}
/** The dollars a level's credits are worth at the nominal value: the most it may ever spend. */
export function nominalBudgetMicroUsd(tier) {
    return AI_MONTHLY_CREDITS[tier] * MICRO_USD_PER_CREDIT;
}
/** The paid levels, as the store's entitlements name them. One entitlement per level. */
export const PAID_TIERS = ['premium', 'ai', 'ai_max'];
/** The highest level among the entitlements an account holds; free when it holds none. */
export function tierFromEntitlements(entitlements) {
    let tier = 'free';
    for (const entitlement of entitlements) {
        if (isPlanTier(entitlement))
            tier = higherTier(tier, entitlement);
    }
    return tier;
}
function option(tier, period) {
    return {
        tier,
        period,
        productId: `orbit_${tier}_${period}`,
        basePlanId: period === 'monthly' ? 'monthly-autorenew' : 'annual-autorenew',
        offering: tier,
        packageType: period === 'monthly' ? '$rc_monthly' : '$rc_annual',
    };
}
/** The six paid options; with the free level, the seven plans. */
export const PLAN_OPTIONS = PAID_TIERS.flatMap((tier) => [
    option(tier, 'monthly'),
    option(tier, 'annual'),
]);
/**
 * The plan a store product is, whichever way the store spells it: `orbit_ai_annual`, or Google
 * Play's `orbit_ai_annual:annual-autorenew`. `undefined` for a product this catalogue does not sell.
 */
export function planOptionFor(productId) {
    const product = productId.split(':')[0]?.trim() ?? '';
    return PLAN_OPTIONS.find((candidate) => candidate.productId === product);
}
/** How many months one charge of a plan pays for. */
export function monthsPerCharge(period) {
    return period === 'annual' ? 12 : 1;
}
/** `anchor` moved on by whole calendar months, keeping its day where the month has it. */
function addMonths(anchor, months) {
    const target = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + months, 1, anchor.getUTCHours(), anchor.getUTCMinutes(), anchor.getUTCSeconds(), anchor.getUTCMilliseconds()));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(anchor.getUTCDate(), lastDay));
    return target.getTime();
}
/**
 * The month of credits `now` falls in.
 *
 * A subscription's credits renew on the day it was bought, every month — an annual plan too, so
 * a year is twelve allowances and not one. With no subscription (`anchor` null) they renew on the
 * first of the month, UTC. A 31st renews on the last day of a shorter month.
 */
export function creditPeriod(anchor, now) {
    const today = new Date(now);
    if (anchor === null || anchor > now) {
        return {
            start: Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
            end: Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1),
        };
    }
    const from = new Date(anchor);
    let months = (today.getUTCFullYear() - from.getUTCFullYear()) * 12 +
        (today.getUTCMonth() - from.getUTCMonth());
    if (addMonths(from, months) > now)
        months -= 1;
    return { start: addMonths(from, months), end: addMonths(from, months + 1) };
}
//# sourceMappingURL=plans.js.map